/**
 * The application's own chrome: the frame every other component sits inside.
 *
 * Replaces a sidebar with a domain list folded into it, a team switcher, a
 * bell whose icons come in six colours, and a coachmark with a pulsing ring.
 */
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Bell, Check, ChevronDown, ChevronRight, Search } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button } from './primitives'
import './app.css'

/* ---------------- The frame ---------------- */

export function AppFrame({ side, top, children }: { side: ReactNode; top: ReactNode; children: ReactNode }) {
  return (
    <div className="ds-app">
      <div className="ds-app__side">{side}</div>
      <div className="ds-app__main">
        <div className="ds-app__top">{top}</div>
        <div className="ds-app__body">{children}</div>
      </div>
    </div>
  )
}

/* ---------------- Getting around ---------------- */

export function SideGroup({ children }: { children: ReactNode }) {
  return <div className="ds-side__group">{children}</div>
}

/**
 * A link says where it goes and, when it matters, how many things are there.
 * The amber dot appears only when something on that page needs a person: it
 * is the same amber as everywhere else, and it means the same thing.
 */
export function SideLink({
  icon,
  children,
  count,
  needsYou,
  current,
  onClick,
  to,
  title,
  after,
  className,
}: {
  icon?: ReactNode
  children: ReactNode
  count?: number
  needsYou?: boolean
  current?: boolean
  onClick?: () => void
  /** Somewhere to go. Without it the row is a button that does something here. */
  to?: string
  title?: string
  /** A mark that belongs to this entry rather than to its state: Beta, a grip. */
  after?: ReactNode
  className?: string
}) {
  const inner = (
    <>
      {icon}
      <span className="ds-side__label">{children}</span>
      {needsYou && <span className="ds-side__dot" aria-label="Needs a look" />}
      {count !== undefined && <span className="ds-side__count">{count}</span>}
      {after}
    </>
  )

  /*
    `current` is passed in rather than worked out from the route, because the
    rules are not the same for every entry: Settings is current anywhere under
    /settings, Roadmap only at /roadmap exactly. NavLink has one rule and
    would quietly change four of them.
  */
  if (to) {
    return (
      <Link
        to={to}
        className={cn('ds-side__link', className)}
        aria-current={current ? 'true' : undefined}
        onClick={onClick}
        title={title}
      >
        {inner}
      </Link>
    )
  }

  return (
    <button
      type="button"
      className={cn('ds-side__link', className)}
      aria-current={current || undefined}
      onClick={onClick}
      title={title}
    >
      {inner}
    </button>
  )
}

/* ---------------- Where am I ---------------- */

export function Crumbs({ trail, here }: { trail: string[]; here: string }) {
  return (
    <nav className="ds-crumbs" aria-label="Where you are">
      {trail.map((t) => (
        <span key={t} className="ds-crumbs__step">
          <button type="button">{t}</button>
          <ChevronRight aria-hidden="true" />
        </span>
      ))}
      <b>{here}</b>
    </nav>
  )
}

/* ---------------- Which workspace, which site ---------------- */

export function Switcher({
  label,
  meta,
  groups,
  footer,
}: {
  label: string
  meta?: string
  groups: { head: string; items: { name: string; meta?: string; current?: boolean }[] }[]
  footer?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="ds-switcher">
      <button type="button" className="ds-switcher__btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="ds-side__label">
          {label}
          {meta && <span className="ds-switcher__meta">{meta}</span>}
        </span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="ds-switcher__panel" role="menu">
          {groups.map((g) => (
            <div key={g.head}>
              <div className="ds-switcher__head">{g.head}</div>
              {g.items.map((i) => (
                <button key={i.name} type="button" className="ds-switcher__item" aria-current={i.current || undefined} role="menuitem">
                  {i.current ? <Check aria-hidden="true" /> : <span style={{ width: 16, flex: 'none' }} />}
                  <span className="ds-side__label">
                    {i.name}
                    {i.meta && <span className="ds-switcher__meta">{i.meta}</span>}
                  </span>
                </button>
              ))}
            </div>
          ))}
          {footer && (
            <>
              <div className="ds-switcher__sep" />
              {footer}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* ---------------- Anything waiting for them ---------------- */

export interface NewsItem {
  what: ReactNode
  when: string
  read?: boolean
}

export function Bellcount({ count, onClick }: { count: number; onClick?: () => void }) {
  return (
    <div className="ds-bell">
      <Button variant="ghost" icon aria-label={`What is new, ${count} unread`} onClick={onClick}>
        <Bell aria-hidden="true" />
      </Button>
      {count > 0 && <span className="ds-bell__count">{count}</span>}
    </div>
  )
}

/**
 * One list, in ink. The application draws six kinds of notification in six
 * colours; what a person needs is what happened, and whether they have seen
 * it, which is one dot.
 */
export function News({ items, onReadAll }: { items: NewsItem[]; onReadAll?: () => void }) {
  return (
    <div className="ds-menu ds-news">
      <div className="ds-news__head">
        <span className="ds-news__title">What is new</span>
        <Button variant="ghost" size="sm" onClick={onReadAll}>
          Mark all as read
        </Button>
      </div>
      <ul className="ds-news__list">
        {items.map((i, n) => (
          <li key={n} className="ds-news__item" data-read={i.read || undefined}>
            <span className="ds-news__unread" aria-label={i.read ? undefined : 'Not read yet'} />
            <div className="ds-news__body">
              <div className="ds-news__what">{i.what}</div>
              <div className="ds-news__when">{i.when}</div>
            </div>
          </li>
        ))}
      </ul>
      <div className="ds-news__foot">
        <Button variant="ghost" size="sm">
          See everything
        </Button>
      </div>
    </div>
  )
}

/* ---------------- People ---------------- */

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')

/** Initials in ink. No colour keyed off somebody's name: colour is a state. */
export function Face({ name, sm }: { name: string; sm?: boolean }) {
  return (
    <span className={cn('ds-face', sm && 'ds-face--sm')} title={name} aria-hidden="true">
      {initials(name)}
    </span>
  )
}

export function Faces({ names, sm }: { names: string[]; sm?: boolean }) {
  return (
    <span className="ds-faces" aria-label={names.join(', ')}>
      {names.map((n) => (
        <Face key={n} name={n} sm={sm} />
      ))}
    </span>
  )
}

/* ---------------- Showing somebody the way ---------------- */

/** A ring around the thing and a card beside it. The ring does not pulse. */
export function Point({
  step,
  of,
  what,
  children,
  onNext,
  onSkip,
}: {
  step: number
  of: number
  what: string
  children: ReactNode
  onNext?: () => void
  onSkip?: () => void
}) {
  return (
    <span className="ds-point">
      {children}
      <span className="ds-point__ring" aria-hidden="true" />
      <span className="ds-point__card" role="dialog" aria-label={what}>
        <span className="ds-point__step">
          Step {step} of {of}
        </span>
        <p className="ds-point__what">{what}</p>
        <span className="ds-point__foot">
          <button type="button" className="ds-point__skip" onClick={onSkip}>
            Skip this
          </button>
          {/* Paper on ink, not the accent. The accent is used as a fill in
              exactly one place in this system, and this is not it. */}
          <Button size="sm" variant="secondary" onClick={onNext}>
            {step === of ? 'Done' : 'Next'}
          </Button>
        </span>
      </span>
    </span>
  )
}

/* ---------------- Walking through a job ---------------- */

export function Flow({
  stops,
  current,
  children,
  back,
  next,
}: {
  stops: string[]
  current: number
  children: ReactNode
  back?: ReactNode
  next?: ReactNode
}) {
  return (
    <div className="ds-flow">
      <div className="ds-flow__rail">
        {stops.map((s, i) => (
          <div key={s} className="ds-flow__stop" data-state={i < current ? 'done' : i === current ? 'now' : 'waiting'}>
            <span className="ds-flow__num" aria-hidden="true">
              {i < current ? <Check /> : i + 1}
            </span>
            <span className="ds-side__label">{s}</span>
          </div>
        ))}
      </div>
      <div className="ds-flow__body">{children}</div>
      <div className="ds-flow__foot">
        <span className="ds-flow__where">
          Step {current + 1} of {stops.length}
        </span>
        <span style={{ display: 'flex', gap: 10 }}>
          {back}
          {next}
        </span>
      </div>
    </div>
  )
}

/* ---------------- Everything, from the keyboard ---------------- */

export function Command({
  groups,
}: {
  groups: { head: string; items: { icon?: ReactNode; label: string; hint?: string; on?: boolean }[] }[]
}) {
  return (
    <div className="ds-menu ds-cmd">
      <div className="ds-cmd__field">
        <Search aria-hidden="true" />
        <input className="ds-cmd__input" placeholder="Search your sites, pages and settings" aria-label="Search" readOnly />
      </div>
      {groups.map((g) => (
        <div key={g.head}>
          <div className="ds-cmd__group">{g.head}</div>
          {g.items.map((i) => (
            <button key={i.label} type="button" className="ds-cmd__item" data-on={i.on || undefined}>
              {i.icon}
              {i.label}
              {i.hint && <span className="ds-cmd__hint">{i.hint}</span>}
            </button>
          ))}
        </div>
      ))}
      <div className="ds-cmd__foot">
        <span>Up and down to move</span>
        <span>Enter to open</span>
        <span>Esc to close</span>
      </div>
    </div>
  )
}
